import { Fits } from "@hscmap/fits";
import axios from 'axios'
import { Globe, tile } from "@hscmap/stellar-globe";
import { TileTextureProviderComponentBase } from "@/stellar-globe-components";
import { mat4 } from "gl-matrix";
import { assert } from "@/utils/error";
import { Component, Prop } from "vue-property-decorator";
import { ImageFilter } from '@/utils/image_filter';

@Component
export class TextureProviderComponent extends TileTextureProviderComponentBase<TextureProvider> {
  @Prop({ required: true })
  src!: string

  @Prop({ required: true })
  params?: IParams

  @Prop({ default: false })
  flipV!: boolean

  created() {
    this.$watch(() => this.params, () => this.syncParms(this.tp), { deep: true })
  }

  private syncParms(tp: TextureProvider) {
    const { params } = this
    if (params) {
      tp.mixer.beta = params.beta
      tp.mixer.a = params.a
      tp.mixer.b = params.b
      tp.mixer.bias = params.bias
      tp.mixer.mix = mix2mat4(params.mix, this.tp.nFilters)
      tp.mixer.exposure = params.exposure
      tp.mixer.gamma = params.gamma
      tp.mixer.ground = params.ground
      this.tp && this.updateParams()
    }
  }

  newTextureProvider() {
    const tp = new TextureProvider(this.globe, this.src, () => {
      this.$emit('load', tp)
      this.syncParms(tp)
    })
    return tp
  }
}

/**
 * composite 4 sources into RGB
 * some sources can be zero
 */
class Mixer extends ImageFilter {
  a = [1, 1, 1, 1]
  b = [0, 0, 0, 0]
  beta = [1, 1, 1, 1]
  bias = [0, 0, 0, 0]
  mix = mat4.create()
  exposure = 1
  gamma = 1
  ground = 0

  fragmentShader() {
    return require('./mixer.frag.glsl') as string
  }

  setUniformParams() {
    this.program.uniform4fv({
      u_a: this.a,
      u_b: this.b,
      u_beta: this.beta,
      u_bias: this.bias,
    })
    this.program.uniformMatrix4fv({
      // @ts-ignore
      u_mix: this.mix,
    })
    this.program.uniform1f({
      u_exposure: this.exposure,
      u_gamma: this.gamma,
      u_ground: this.ground,
    })
  }
}

interface IMeta {
  wcs: {},
  filters: string[],
  tractOptions?: Partial<{
    flipV: boolean,
    minTileLevel: number,
    maxTileLevel: number,
    tileSize: number,
  }>
}

class TextureProvider extends tile.AsyncTextureProvider {
  readonly mixer: Mixer
  meta?: IMeta
  nFilters = -1

  constructor(globe: Globe, readonly url: string, readonly onload?: () => void) {
    super(globe)
    this.mixer = new Mixer(this.globe.gl)
    try {
      this.globe.gl.getExtension('OES_texture_float')
      this.loadMetadata()
    } catch {
      console.warn(`not supported: OES_texture_float`)
    }
  }

  release() {
    this.mixer.release()
    super.release()
  }

  private tract?: tile.Tract

  private async loadMetadata() {
    const { data: meta } = await axios.get<IMeta>(`${this.url}/meta.json`)
    this.meta = meta
    this.tract = tile.Tract.fromFitsHeader(meta.wcs, meta.tractOptions)
    this.nFilters = meta.filters.length
    assert(this.nFilters <= 4, `nFilters <= 4: (${this.nFilters})`)
    this.onload && this.onload()
  }

  walkTracts(cb: (tract: tile.Tract) => void) {
    if (this.tract) {
      cb(this.tract)
    }
  }

  async makeTileTexture(ref: tile.TileRef) {
    const tt = new tile.TileTexture(this)
    const filters = this.meta!.filters
    const urls = filters.map((f) => `${this.url}/${f}/${ref.level}/${ref.p}/${ref.q}.fits`)
    const hdus = await Promise.all(urls.map(async (url) => {
      const hdul =
        await Fits.fetch(url, [{ outputDataType: Fits.DataType.float32, sourceIndex: 0 }]).catch(() => undefined)
      return hdul && hdul[0]
    }))

    const gl = this.globe.gl
    const { tileSize } = ref.tract

    try { // TODO: remove
      this.mixer.apply(tt.tex, tileSize, tileSize, 4, (t, i) => {
        t.bind(() => {
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
          if (i < this.nFilters && hdus[i]) {
            gl.texImage2D(
              gl.TEXTURE_2D, 0, gl.LUMINANCE, tileSize, tileSize, 0, gl.LUMINANCE, gl.FLOAT, hdus[i]!.float32array())
          } else {
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.FLOAT, new Float32Array([0]))
          }
        })
      })
    } catch (e) {
      console.error(e)
    }

    tt.tex.bind(() => {
      if (ref.level === ref.tract.maxTileLevel) {
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
      }
    })

    return tt
  }
}

interface IParams {
  a: number[],
  b: number[],
  beta: number[],
  bias: number[],
  mix: number[],
  exposure: number,
  gamma: number,
  ground: number,
}

function mix2mat4(a: number[], n: number) {
  const b = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]
  for (let i = 0; i < 3; ++i) {
    for (let j = 0; j < n; ++j) {
      b[4 * j + i] = a[3 * j + i]
    }
  }
  return mat4.fromValues(
    b[0], b[1], b[2], b[3],
    b[4], b[5], b[6], b[7],
    b[8], b[9], b[10], b[11],
    b[12], b[13], b[14], b[15],
  )
}
